/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { APIGateways } from "../../../utility/APIGateways";
import { getAuthHeader } from "../../../utility/auth";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
const COMPONENT_NAME = "Admin lexBotManager/services/LexBotService";

/**
 * Gets all the Associated Lex Bots from amazon connect AWS.
 */
export const getAssociatedLexBotsV2FromConnect = async () => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}/associated`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error loading associated lex bots V2`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_ASSOCIATED_LEX_BOTS_V2_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * Gets all the Lex Bots V2 from AWS.
 */
export const getAllLexBotsV2 = async () => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error loading lex bots V2 list`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_LEX_BOT_V2_LIST_ERROR_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Get details for Lex Bot
 */
export const getLexBotV2Details = async (botId) => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}/${botId}`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting lex bot V2 details`, {
			additionalData: {
				error: errorToObject(error),
				botId,
			},
			logCode: errorCodes.GET_LEX_BOT_V2_DETAILS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Get language for Lex Bot
 */
export const getLexBotV2Locales = async (botId, botVersion) => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}/${botId}/${botVersion}/locale`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting lex bot V2 locale`, {
			additionalData: {
				error: errorToObject(error),
				botId,
			},
			logCode: errorCodes.GET_LEX_BOT_V2_LOCALES_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Associate lex bot to connect
 */
export const associateLexBotV2ToConnect = async (botId, aliasId) => {
	try {
		let response = await axios.post(
			`${APIGateways.lexBotsV2Endpoint}/associate`,
			{ botId, aliasId },
			{
				headers: await getAuthHeader(),
			},
		);
		return response.data.result;
	} catch (error) {
		v2.error(`Error associating lex bot V2 to connect`, {
			additionalData: {
				error: errorToObject(error),
				botId,
				aliasId,
			},
			logCode: errorCodes.ASSOCIATE_LEX_BOT_V2_TO_CONNECT_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Disassociate lex bot to connect
 */
export const disassociateLexBotV2ToConnect = async (botId, aliasId) => {
	try {
		let response = await axios.post(
			`${APIGateways.lexBotsV2Endpoint}/disassociate`,
			{ botId, aliasId },
			{
				headers: await getAuthHeader(),
			},
		);
		return response.data.result;
	} catch (error) {
		v2.error(`Error disassociating lex bot V2 from connect`, {
			additionalData: {
				error: errorToObject(error),
				botId,
				aliasId,
			},
			logCode: errorCodes.DISASSOCIATE_LEX_BOT_V2_FROM_CONNECT_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * Get lex bot aliases
 */
export const getLexBotV2Aliases = async (botId) => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}/${botId}/alias`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting lex bot V2 aliases.`, {
			additionalData: {
				error: errorToObject(error),
				botId,
			},
			logCode: errorCodes.GET_LEX_BOT_V2_ALIASES_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * Get lex bot intents
 */
export const getLexBotV2LanguageIntents = async (botId, botVersion, localeId) => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}/${botId}/${botVersion}/${localeId}/intents`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting lex bot V2 language intents.`, {
			additionalData: {
				error: errorToObject(error),
				botId,
			},
			logCode: errorCodes.GET_LEX_BOT_V2_INTENTS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * Get lex bot Slot types
 */
export const getAllLexBotV2LanguageSlotTypes = async (botId, botVersion, localeId) => {
	try {
		let response = await axios.get(`${APIGateways.lexBotsV2Endpoint}/${botId}/${botVersion}/${localeId}/slots`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting all lex bot V2 language slot types.`, {
			additionalData: {
				error: errorToObject(error),
				botId,
			},
			logCode: errorCodes.GET_LEX_BOT_V2_SLOT_TYPES_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
